import { useEffect, useState } from "react";
import { Outlet, useSearchParams } from "react-router-dom";
import liff from "@line/liff";

import { LogSystemApi } from "@utils/api/analytics/log-system.api";
import { WallpaperWebApi } from "@utils/api/wallpaper/web-client.api";
import { logWallpaperPage } from "./wallpaperLog";
import {
	isLiffFlow,
	resolveParam,
	persistParam,
	WALLPAPER_COUNTRY_KEY,
	WALLPAPER_FROM_KEY,
} from "./wallpaperParams";

/**
 * 桌布活動的登入 Guard。
 *
 * 版本分流只看 LIFF 入口（WallpaperLiffPage）留下的 from 標記，不看裝置上的 LINE 登入狀態，
 * 所以同一支手機先開 LIFF 版再開國際版連結，也不會被誤判成 LIFF 版。
 * 兩種來源各自換發自己的 token，互不影響：
 *  - 台灣 LIFF 版：用真實 profile / access_token 打 log system 的 /liff/login，
 *    token 存 localStorage 的 log_token，數據也打 log system。
 *  - 國際 web 版：沒有 LINE 身分，改用裝置 uuid 打 /wallpaper/web-client/login
 *    換 access_token（存 wallpaper_web_token），之後上傳圖片與數據都帶這個 token。
 */

const LOG_TOKEN_KEY = "log_token";
/** 驗證 / 上報最多等這麼久，逾時就先放行——不能把使用者卡在空白頁。 */
const INIT_TIMEOUT_MS = 8000;

const WEB_DOCUMENT_TITLE = "TORY BURCH";

export default function WallpaperLogLoginGuard() {
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [searchParams] = useSearchParams();

	// 記錄一次頁面瀏覽，同時可驗證目前 token 是否有效
	// （logWallpaperPage 不會拋錯，失敗回 false 代表 token 需要重換）
	const checkEventToken = () => logWallpaperPage({ path: "/wallpaper/login" });

	// 國際 web 版的來源：網址的 country，沒帶就預設香港
	const persistWebFrom = () => {
		const country = resolveParam(
			searchParams.get("country"),
			WALLPAPER_COUNTRY_KEY,
		);
		persistParam(WALLPAPER_FROM_KEY, country || "hk");
	};

	// 換發登入 token（LIFF 版：log_token；web 版：wallpaper_web_token）
	const getLoginToken = async (isLiff: boolean) => {
		try {
			if (!isLiff) {
				await WallpaperWebApi.login();
				return;
			}

			// 可能是整頁 reload 直接進來（此頁載入還沒 init 過），先確保 SDK 就緒
			await liff.init({ liffId: process.env.REACT_APP_LIFF_ID as string });
			const profile = await liff.getProfile();
			const res = await LogSystemApi.loginEvent({
				line_uid: profile.userId,
				line_name: profile.displayName,
				liff_id: process.env.REACT_APP_LIFF_ID ?? "",
				access_token: liff.getAccessToken() ?? "",
			});

			if (res?.jwt) {
				localStorage.setItem(LOG_TOKEN_KEY, res.jwt);
			}
		} catch (error) {
			console.error("登入失敗:", error);
		} finally {
			setIsAuthenticated(true);
		}
	};

	const refreshTokenAndCheckEvent = async (isLiff: boolean) => {
		await getLoginToken(isLiff);
		await checkEventToken();
	};

	const initialize = async () => {
		// 版本分流：只認 LIFF 入口留下的標記，其餘一律國際 web 版（並補寫 from）。
		const isLiff = isLiffFlow();
		if (!isLiff) {
			persistWebFrom();
			document.title = WEB_DOCUMENT_TITLE;
		}

		const hasExistingToken = isLiff
			? !!localStorage.getItem(LOG_TOKEN_KEY)
			: WallpaperWebApi.hasValidToken();

		if (hasExistingToken) {
			const isValidToken = await checkEventToken();
			if (isValidToken) {
				setIsAuthenticated(true);
				return;
			}
			await refreshTokenAndCheckEvent(isLiff);
		} else {
			await refreshTokenAndCheckEvent(isLiff);
		}
	};

	useEffect(() => {
		const timer = setTimeout(() => setIsAuthenticated(true), INIT_TIMEOUT_MS);
		initialize()
			.catch((error) => {
				console.error("[wallpaper-log-login] 初始化失敗（已放行）", error);
			})
			.finally(() => {
				clearTimeout(timer);
				setIsAuthenticated(true);
			});
		return () => clearTimeout(timer);
		// 僅初始化跑一次
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return isAuthenticated ? <Outlet /> : null;
}
